// View Poll Web Component
// Encapsulated component for displaying polls to end users

class ViewPoll extends HTMLElement {
  constructor() {
    super();
    this.pollData = null;
    this.currentUser = null;
    this.isVoting = false;
    this.isClosing = false;
  }

  static get observedAttributes() {
    return ['poll-data', 'current-user'];
  }

  connectedCallback() {
    const pollDataAttr = this.getAttribute('poll-data');
    const currentUserAttr = this.getAttribute('current-user');

    if (pollDataAttr) {
      try {
        this.pollData = JSON.parse(pollDataAttr);
      } catch (error) {
        window.logger?.error('Invalid poll data in view-poll:', error);
      }
    }

    if (currentUserAttr) {
      try {
        this.currentUser = JSON.parse(currentUserAttr);
      } catch (error) {
        window.logger?.error('Invalid current user data in view-poll:', error);
      }
    }

    this.render();
    this.attachEventListeners();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'poll-data' && newValue) {
      try {
        this.pollData = JSON.parse(newValue);
        this.render();
        this.attachEventListeners();
      } catch (error) {
        window.logger?.error('Invalid poll data:', error);
      }
    } else if (name === 'current-user' && newValue) {
      try {
        this.currentUser = JSON.parse(newValue);
        this.render();
        this.attachEventListeners();
      } catch (error) {
        window.logger?.error('Invalid current user data:', error);
      }
    }
  }

  setPollData(data) {
    this.pollData = data;
    this.render();
    this.attachEventListeners();
  }

  setCurrentUser(user) {
    this.currentUser = user;
    this.render();
    this.attachEventListeners();
  }

  render() {
    if (!this.pollData) {
      this.innerHTML = '<div class="loading">Loading poll...</div>';
      return;
    }

    const {
      poll_question,
      poll_options = [],
      poll_closed,
      poll_closed_at,
      poll_type = 'single',
      total_votes,
      user_voted,
      can_see_results,
      is_owner
    } = this.pollData;

    const showResults = can_see_results || poll_closed;
    const canVote = !poll_closed && !user_voted;
    const canChangeVote = !poll_closed && user_voted;
    const canClose = !poll_closed && (is_owner === true || is_owner === 1);

    // Get selected option IDs for multi-select
    const selectedOptionIds = poll_options
      .filter(opt => opt.user_voted === true)
      .map(opt => opt.id);

    this.innerHTML = `
      <div class="view-poll">
        <div class="poll-header">
          <div class="poll-title-row">
            <h3 class="poll-question">${this.escapeHtml(poll_question || 'Poll')}</h3>
            <span class="poll-type-badge poll-type-${poll_type}">
              ${poll_type === 'single' ? 'Single Selection' : poll_type === 'multi' ? 'Multi-Select' : 'Toggle'}
            </span>
          </div>
          ${poll_closed ? `
            <div class="poll-status closed">
              <span class="status-icon">🔒</span>
              <span class="status-text">Poll Closed</span>
              ${poll_closed_at ? `
                <span class="closed-date">${this.formatDate(poll_closed_at)}</span>
              ` : ''}
            </div>
          ` : ''}
        </div>

        <div class="poll-options-container poll-type-${poll_type}">
          ${poll_options.map((option) => {
    const isUserVote = option.user_voted === true;
    const userVoteState = option.user_vote_state; // 'yes', 'no', or null for toggle
    const voteCount = showResults ? (option.vote_count || 0) : null;
    const yesCount = showResults && poll_type === 'toggle' ? (option.yes_count || 0) : null;
    const noCount = showResults && poll_type === 'toggle' ? (option.no_count || 0) : null;
    const percentage = showResults && total_votes > 0 && poll_type !== 'toggle' ? (option.percentage || 0) : null;
    const isSelected = selectedOptionIds.includes(option.id);

    if (poll_type === 'multi') {
      // Multi-select: render as checkboxes
      return `
              <div class="poll-option poll-option-multi ${isSelected ? 'selected' : ''} ${poll_closed ? 'closed' : ''}" 
                   data-option-id="${option.id}">
                <div class="option-content">
                  ${!poll_closed && (canVote || canChangeVote) ? `
                    <label class="multi-select-option">
                      <input 
                        type="checkbox" 
                        class="poll-checkbox"
                        data-option-id="${option.id}"
                        ${isSelected ? 'checked' : ''}
                        ${this.isVoting ? 'disabled' : ''}
                      />
                      <span class="option-text">${this.escapeHtml(option.option_text)}</span>
                    </label>
                  ` : `
                    <div class="option-display ${showResults ? 'has-results' : ''}">
                      <span class="option-text">${this.escapeHtml(option.option_text)}</span>
                      ${showResults ? `
                        <span class="vote-stats-inline">
                          <span class="vote-count">${voteCount} ${voteCount === 1 ? 'vote' : 'votes'}</span>
                          ${percentage !== null ? `<span class="vote-percentage">${percentage}%</span>` : ''}
                          ${isSelected ? '<span class="vote-indicator">✓ Your selection</span>' : ''}
                        </span>
                      ` : (isSelected ? '<span class="vote-indicator">✓ Your selection</span>' : '')}
                    </div>
                  `}
                  
                  ${showResults ? `
                    <div class="option-results">
                      <div class="vote-bar-container">
                        <div 
                          class="vote-bar" 
                          style="width: ${percentage || 0}%"
                          role="progressbar"
                          aria-valuenow="${percentage || 0}"
                          aria-valuemin="0"
                          aria-valuemax="100"
                        ></div>
                      </div>
                    </div>
                  ` : ''}
                </div>
              </div>
            `;
    } else if (poll_type === 'toggle') {
      // Toggle: explicit Yes / No buttons per option
      const toggleState = userVoteState || null; // 'yes', 'no', or null
      const isYes = toggleState === 'yes';
      const isNo = toggleState === 'no';

      return `
              <div class="poll-option poll-option-toggle ${poll_closed ? 'closed' : ''}" 
                   data-option-id="${option.id}">
                <div class="option-content">
                  ${!poll_closed && (canVote || canChangeVote) ? `
                    <div class="toggle-option-row">
                      <span class="option-text">${this.escapeHtml(option.option_text)}</span>
                      <div class="toggle-yes-no-buttons">
                        <button 
                          type="button" 
                          class="toggle-yes-btn ${isYes ? 'selected' : ''}"
                          data-option-id="${option.id}"
                          data-vote-state="yes"
                          ${this.isVoting ? 'disabled' : ''}
                          title="Yes"
                        >✓ Yes</button>
                        <button 
                          type="button" 
                          class="toggle-no-btn ${isNo ? 'selected' : ''}"
                          data-option-id="${option.id}"
                          data-vote-state="no"
                          ${this.isVoting ? 'disabled' : ''}
                          title="No"
                        >✗ No</button>
                      </div>
                    </div>
                  ` : `
                    <div class="option-display ${showResults ? 'has-results' : ''}">
                      <span class="option-text">${this.escapeHtml(option.option_text)}</span>
                      ${toggleState ? `<span class="vote-indicator vote-indicator-${toggleState}">${isYes ? '✓' : '✗'} Your ${toggleState === 'yes' ? 'yes' : 'no'}</span>` : ''}
                    </div>
                  `}
                </div>
                ${showResults ? `
                  <div class="toggle-count-row">
                    ( <span class="toggle-count-yes">${yesCount}</span> / <span class="toggle-count-no">${noCount}</span> )
                  </div>
                ` : ''}
              </div>
            `;
    } else {
      // Single-select: render as buttons (existing behavior)
      return `
              <div class="poll-option poll-option-single ${isUserVote ? 'user-voted' : ''} ${poll_closed ? 'closed' : ''}" 
                   data-option-id="${option.id}">
                <div class="option-content">
                  ${!poll_closed && (canVote || canChangeVote) ? `
                    <button 
                      type="button" 
                      class="option-button ${isUserVote ? 'selected' : ''}"
                      data-option-id="${option.id}"
                      ${this.isVoting ? 'disabled' : ''}
                    >
                      <span class="option-text">${this.escapeHtml(option.option_text)}</span>
                      ${isUserVote ? '<span class="vote-indicator">✓</span>' : ''}
                    </button>
                  ` : `
                    <div class="option-display ${showResults ? 'has-results' : ''}">
                      <span class="option-text">${this.escapeHtml(option.option_text)}</span>
                      ${showResults ? `
                        <span class="vote-stats-inline">
                          <span class="vote-count">${voteCount} ${voteCount === 1 ? 'vote' : 'votes'}</span>
                          ${percentage !== null ? `<span class="vote-percentage">${percentage}%</span>` : ''}
                          ${isUserVote ? '<span class="vote-indicator">✓ Your vote</span>' : ''}
                        </span>
                      ` : (isUserVote ? '<span class="vote-indicator">✓ Your vote</span>' : '')}
                    </div>
                  `}
                  
                  ${showResults ? `
                    <div class="option-results">
                      <div class="vote-bar-container">
                        <div 
                          class="vote-bar" 
                          style="width: ${percentage || 0}%"
                          role="progressbar"
                          aria-valuenow="${percentage || 0}"
                          aria-valuemin="0"
                          aria-valuemax="100"
                        ></div>
                      </div>
                    </div>
                  ` : ''}
                </div>
              </div>
            `;
    }
  }).join('')}
        </div>

        ${showResults && poll_type !== 'toggle' && total_votes !== null ? `
          <div class="poll-footer">
            <div class="total-votes">
              <strong>${total_votes}</strong> ${total_votes === 1 ? 'vote' : 'total votes'}
            </div>
            ${poll_type === 'multi' && selectedOptionIds.length > 0 ? `
              <div class="selected-count">
                You selected <strong>${selectedOptionIds.length}</strong> ${selectedOptionIds.length === 1 ? 'option' : 'options'}
              </div>
            ` : ''}
          </div>
        ` : ''}

        ${canChangeVote && poll_type === 'single' ? `
          <div class="poll-note">
            <span class="note-icon">💡</span>
            <span class="note-text">Click another option to change your vote</span>
          </div>
        ` : ''}
        ${poll_type === 'multi' && !poll_closed ? `
          <div class="poll-note">
            <span class="note-icon">💡</span>
            <span class="note-text">Select multiple options, then click "Submit Vote"</span>
          </div>
        ` : ''}
        ${poll_type === 'multi' && selectedOptionIds.length === 0 && !poll_closed && (canVote || canChangeVote) ? `
          <div class="poll-note poll-note-warning">
            <span class="note-icon">⚠️</span>
            <span class="note-text">Please select at least one option</span>
          </div>
        ` : ''}
        ${poll_type === 'toggle' && !poll_closed && (canVote || canChangeVote) ? `
          <div class="poll-note">
            <span class="note-icon">💡</span>
            <span class="note-text">Click <strong>Yes</strong> or <strong>No</strong> for each option. Click the same button again to clear.</span>
          </div>
        ` : ''}

        ${canClose ? `
          <div class="poll-actions">
            <button 
              type="button" 
              class="btn btn-secondary close-poll-btn"
              ${this.isClosing ? 'disabled' : ''}
            >
              ${this.isClosing ? 'Closing...' : 'Close Poll'}
            </button>
          </div>
        ` : ''}

        <div class="alert-container"></div>
      </div>
    `;
  }

  attachEventListeners() {
    const pollType = this.pollData?.poll_type || 'single';

    if (pollType === 'multi') {
      // Multi-select: handle checkbox changes
      this.querySelectorAll('.poll-checkbox').forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          this.handleMultiSelectChange();
        });
      });

      // Add submit button for multi-select if not closed
      const canVote = !this.pollData?.poll_closed && !this.pollData?.user_voted;
      const canChangeVote = !this.pollData?.poll_closed && this.pollData?.user_voted;
      if (!this.pollData?.poll_closed && (canVote || canChangeVote)) {
        const existingSubmit = this.querySelector('.submit-multi-vote-btn');
        if (!existingSubmit) {
          const submitBtn = document.createElement('button');
          submitBtn.type = 'button';
          submitBtn.className = 'btn btn-primary submit-multi-vote-btn';
          submitBtn.textContent = 'Submit Vote';
          submitBtn.disabled = this.isVoting;
          submitBtn.addEventListener('click', () => {
            this.submitMultiSelectVote();
          });
          const actionsDiv = this.querySelector('.poll-actions') || this.querySelector('.poll-footer');
          if (actionsDiv) {
            actionsDiv.insertBefore(submitBtn, actionsDiv.firstChild);
          } else {
            this.querySelector('.poll-options-container').insertAdjacentElement('afterend', submitBtn);
          }
        }
      }
    } else if (pollType === 'toggle') {
      // Toggle: handle Yes / No button clicks
      this.querySelectorAll('.toggle-yes-btn, .toggle-no-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const b = e.currentTarget;
          const optionId = parseInt(b.dataset.optionId, 10);
          const clickState = b.dataset.voteState;
          if (!optionId || !clickState) return;
          const option = this.pollData?.poll_options?.find(o => o.id === optionId);
          const currentState = option?.user_vote_state ?? null;
          const newState = currentState === clickState ? null : clickState;
          this.toggleOption(optionId, newState);
        });
      });
    } else {
      // Single-select: handle button clicks (existing behavior)
      this.querySelectorAll('.option-button').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const optionId = parseInt(e.target.closest('[data-option-id]').dataset.optionId, 10);
          this.voteOnPoll(optionId);
        });
      });
    }

    // Close poll button
    const closeBtn = this.querySelector('.close-poll-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.closePoll();
      });
    }
  }

  async voteOnPoll(optionId) {
    if (this.isVoting || !this.pollData) return;

    this.isVoting = true;
    this.clearAlerts();
    this.updateVotingState(true);

    try {
      const updatedPoll = await window.API.polls.vote(this.pollData.id, optionId);

      // Update poll data
      this.pollData = updatedPoll;
      this.render();
      this.attachEventListeners();

      // Show success feedback
      this.showAlert('Vote recorded! 🎉', 'success');

    } catch (error) {
      window.logger.error('Error voting on poll:', error);
      let errorMessage = 'Failed to vote. Please try again.';

      if (error.message) {
        errorMessage = error.message;
      }

      this.showAlert(errorMessage, 'error');
    } finally {
      this.isVoting = false;
      this.updateVotingState(false);
    }
  }

  /**
   * Handle checkbox changes for multi-select polls
   * Updates UI state and validation messages
   */
  handleMultiSelectChange() {
    const selectedOptionIds = this.getSelectedOptionIds();
    const submitBtn = this.querySelector('.submit-multi-vote-btn');

    // Enable/disable submit button based on selection
    if (submitBtn) {
      submitBtn.disabled = selectedOptionIds.length === 0 || this.isVoting;
    }

    // Update warning message visibility
    const warningNote = this.querySelector('.poll-note-warning');
    if (selectedOptionIds.length === 0 && !this.pollData?.poll_closed) {
      if (!warningNote) {
        // Warning will be shown in render, but we can trigger a re-render if needed
        // For now, just ensure the button is disabled
      }
    } else if (warningNote) {
      warningNote.remove();
    }
  }

  /**
   * Get currently selected option IDs from checkboxes
   */
  getSelectedOptionIds() {
    const checkboxes = this.querySelectorAll('.poll-checkbox:checked');
    return Array.from(checkboxes).map(cb => parseInt(cb.dataset.optionId, 10));
  }

  /**
   * Submit vote for multi-select poll
   */
  async submitMultiSelectVote() {
    if (this.isVoting || !this.pollData) return;

    const selectedOptionIds = this.getSelectedOptionIds();

    // Validate at least one option is selected
    if (selectedOptionIds.length === 0) {
      this.showAlert('Please select at least one option', 'error');
      return;
    }

    this.isVoting = true;
    this.clearAlerts();
    this.updateVotingState(true);

    try {
      // Submit vote with array of option IDs
      const updatedPoll = await window.API.polls.vote(this.pollData.id, selectedOptionIds);

      // Update poll data
      this.pollData = updatedPoll;
      this.render();
      this.attachEventListeners();

      // Show success feedback
      this.showAlert('Vote recorded! 🎉', 'success');

    } catch (error) {
      window.logger.error('Error voting on multi-select poll:', error);
      let errorMessage = 'Failed to vote. Please try again.';

      if (error.message) {
        errorMessage = error.message;
      }

      this.showAlert(errorMessage, 'error');
    } finally {
      this.isVoting = false;
      this.updateVotingState(false);
    }
  }

  /**
   * Vote on a toggle poll: set Yes, No, or clear (null) for an option.
   * Clicking the same button again clears your vote.
   */
  async toggleOption(optionId, voteState) {
    if (this.isVoting || !this.pollData) return;

    this.isVoting = true;
    this.clearAlerts();
    this.updateVotingState(true);

    try {
      const updatedPoll = await window.API.polls.vote(this.pollData.id, {
        option_id: optionId,
        vote_state: voteState
      });

      this.pollData = updatedPoll;
      this.render();
      this.attachEventListeners();

      const msg = voteState === null
        ? 'Vote cleared'
        : `Marked ${voteState === 'yes' ? 'Yes' : 'No'}! 🎉`;
      this.showAlert(msg, 'success');
    } catch (error) {
      window.logger.error('Error voting on toggle poll:', error);
      const errorMessage = error?.message ?? 'Failed to vote. Please try again.';
      this.showAlert(errorMessage, 'error');
    } finally {
      this.isVoting = false;
      this.updateVotingState(false);
    }
  }

  async closePoll() {
    if (this.isClosing || !this.pollData) return;

    // Confirm before closing
    if (!confirm('Are you sure you want to close this poll? It cannot be reopened.')) {
      return;
    }

    this.isClosing = true;
    this.clearAlerts();
    this.updateClosingState(true);

    try {
      const updatedPoll = await window.API.polls.close(this.pollData.id);

      // Update poll data
      this.pollData = updatedPoll;
      this.render();
      this.attachEventListeners();

      // Show success feedback
      this.showAlert('Poll closed successfully', 'success');

      // Dispatch event to notify parent
      this.dispatchEvent(new CustomEvent('poll-closed', {
        detail: { poll: updatedPoll },
        bubbles: true
      }));

    } catch (error) {
      window.logger.error('Error closing poll:', error);
      let errorMessage = 'Failed to close poll. Please try again.';

      if (error.message) {
        errorMessage = error.message;
      }

      this.showAlert(errorMessage, 'error');
    } finally {
      this.isClosing = false;
      this.updateClosingState(false);
    }
  }

  updateVotingState(isVoting) {
    const buttons = this.querySelectorAll(
      '.option-button, .toggle-button, .toggle-yes-btn, .toggle-no-btn'
    );
    buttons.forEach(btn => {
      btn.disabled = isVoting;
    });
    const checkboxes = this.querySelectorAll('.poll-checkbox');
    checkboxes.forEach(cb => {
      cb.disabled = isVoting;
    });
    const submitBtn = this.querySelector('.submit-multi-vote-btn');
    if (submitBtn) {
      submitBtn.disabled = isVoting;
    }
  }

  updateClosingState(isClosing) {
    const closeBtn = this.querySelector('.close-poll-btn');
    if (closeBtn) {
      closeBtn.disabled = isClosing;
      closeBtn.textContent = isClosing ? 'Closing...' : 'Close Poll';
    }
  }

  showAlert(message, type = 'info') {
    const container = this.querySelector('.alert-container');
    if (!container) return;

    const alert = document.createElement('div');
    alert.className = `alert alert-${type}`;
    alert.textContent = message;

    container.appendChild(alert);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (alert.parentNode) {
        alert.remove();
      }
    }, 5000);
  }

  clearAlerts() {
    const container = this.querySelector('.alert-container');
    if (container) {
      container.innerHTML = '';
    }
  }

  formatDate(dateString) {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch (_error) {
      return dateString;
    }
  }

  escapeHtml(text) {
    if (text === null || text === undefined) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Register the custom element
customElements.define('view-poll', ViewPoll);
